/** Server error codes from `create-magic-link-session`. */
export type MagicLinkSessionErrorCode =
  | 'invalid_magic_link_token'
  | 'magic_link_token_required'
  | 'magic_link_expired'
  | 'magic_link_already_used'
  | 'profile_not_linked'
  | 'profile_not_found'
  | 'profile_email_missing'
  | 'account_deactivated'
  | 'origin_not_allowed'
  | 'session_token_failed'
  | 'server_misconfigured'
  | 'internal_error'
  | 'profile_id_required';

const SESSION_ERROR_MESSAGES: Record<MagicLinkSessionErrorCode, string> = {
  invalid_magic_link_token:
    'This sign-in link is invalid. Please request a new one from your booking message.',
  magic_link_token_required:
    'This sign-in link is missing its token. Please use the full link from your message.',
  magic_link_expired: 'This link has expired. Please request a new one.',
  magic_link_already_used: 'This login link has already been used. Please request a new one.',
  profile_not_linked: 'We could not match this link to your customer profile. Please contact support.',
  profile_not_found: 'We could not find your customer profile. Please contact support.',
  profile_email_missing: 'Your profile is missing a sign-in email. Please contact support.',
  account_deactivated: 'This account is deactivated. Please contact support.',
  origin_not_allowed:
    'This site is not allowed to complete sign-in. Please open the link on the official website.',
  session_token_failed: 'Could not establish a secure session. Please request a new login link.',
  server_misconfigured: 'Sign-in is temporarily unavailable. Please try again shortly.',
  internal_error: 'Could not establish a secure session. Please request a new login link.',
  profile_id_required: 'Could not establish a secure session. Please request a new login link.',
};

export function mapMagicLinkSessionError(code: string | undefined | null): string {
  if (!code) {
    return SESSION_ERROR_MESSAGES.invalid_magic_link_token;
  }
  const mapped = SESSION_ERROR_MESSAGES[code as MagicLinkSessionErrorCode];
  if (mapped) return mapped;
  return SESSION_ERROR_MESSAGES.internal_error;
}

/** Prefer AuthContext sentences; map raw server codes otherwise. */
export function formatMagicLinkAuthError(error: unknown): string {
  const raw = error instanceof Error ? error.message.trim() : String(error ?? '').trim();
  if (raw && raw.includes(' ')) {
    return raw;
  }
  return mapMagicLinkSessionError(raw || 'invalid_magic_link_token');
}

export type MagicLinkSessionProfile = {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  role: string | null;
  is_active: boolean | null;
  created_at: string | null;
  organization_id: string | null;
};

export function readMagicLinkSessionProfile(payload: unknown): MagicLinkSessionProfile | null {
  if (!payload || typeof payload !== 'object') return null;
  const row = payload as Record<string, unknown>;
  const nested =
    row.profile && typeof row.profile === 'object' ? (row.profile as Record<string, unknown>) : row;
  const id = typeof nested.id === 'string' ? nested.id.trim() : '';
  if (!id) return null;
  return {
    id,
    email: typeof nested.email === 'string' ? nested.email : null,
    first_name: typeof nested.first_name === 'string' ? nested.first_name : null,
    last_name: typeof nested.last_name === 'string' ? nested.last_name : null,
    phone: typeof nested.phone === 'string' ? nested.phone : null,
    role: typeof nested.role === 'string' ? nested.role : null,
    is_active: typeof nested.is_active === 'boolean' ? nested.is_active : null,
    created_at: typeof nested.created_at === 'string' ? nested.created_at : null,
    organization_id: typeof nested.organization_id === 'string' ? nested.organization_id : null,
  };
}
